// React Compiler opt-out: this file still reads mutable external state during render
// (legacy store object getters) that the useFluxStore bridge doesn't cover.
// Remove after migrating those reads (see flux-bridge-conversion skill).
'use no memo';

import {useFluxStore} from 'client/framework';
import React from 'react';
import {Link} from 'react-router';

import {Anchor, Banner, Button, Icon, Menu, Text} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';
import type {QueryBuilder} from '@albert-io/json-api-framework/request/builder';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {EDGE_MENU_POPPER_PROPS} from 'client/TeacherDashboard/config/menuPopperProps';
import useAreLatestQueriesReady from 'lib/hooks/useAreLatestQueriesReady';
import {pluralize} from 'lib/stringUtils';

// Minimal shape for the untyped legacy classroom model — only what we read.
interface ImportedClassroom {
  getId: () => string;
  getName: () => string;
  getSchool: () => {getId: () => string; getName: () => string} | null | undefined;
  getMeta: () => {isCanEnableLicense: () => boolean};
}

/** localStorage key the atomic Banner uses to remember a dismissal, per teacher and school. */
export const getDismissKey = (userId: string, schoolId: string) =>
  `unlicensed-clever-imports-banner:${userId}:${schoolId}`;

// The nested school_v5 filter keeps only schools where the teacher is approved, so one request
// answers the whole question. can_enable_license says whether licensing fits under the seat cap.
const getUnlicensedImportsQuery = () => {
  const teacherId = masqueradeStore.getUserIdByMasqueradeState();
  return resource('classrooms_v1')
    .mandarkEndpoint(['teachers_v1', teacherId, 'classrooms_v1'])
    .include('school_v5')
    .withMeta('classroom_v1')
    .customQuery({meta: {context: {can_enable_license: true}}})
    .filter({
      status: 'active',
      apply_students_to_school_license: false,
      clever_id: {null: false},
      school_v5: {school_personnel_v1: {teacher_v1: {id: teacherId}, status: 'approved'}}
    })
    .filter({school_v5: masqueradeStore.getSchoolId()}, masqueradeStore.isMasquerading())
    .pageSize(100);
};

const MENU_PANEL_CLASS =
  '[&_.o-menu-content]:max-w-[304px]! [&_.o-menu-content]:max-h-[320px]! [&_.o-menu-content]:overflow-y-auto!';

const getSettingsPath = (classroomId: string) => `/classes/${classroomId}/settings/details`;

/** Lists the classes so the teacher can open one's settings; /classes would only redirect to Home. */
const ClassPicker = ({classrooms}: {classrooms: ImportedClassroom[]}) => (
  <Menu closeOnItemClick popperProps={EDGE_MENU_POPPER_PROPS} position='bottom-end' positionFixed>
    <Menu.Trigger className='mr-x1 shrink-0'>
      {(triggerProps) => (
        <Button
          {...triggerProps}
          className='gap-x1 flex items-center'
          color='inherit'
          size='s'
          variant='outlined'
        >
          Open class settings
          <Icon color='inherit' icon='caret-down' />
        </Button>
      )}
    </Menu.Trigger>
    <Menu.Content className={MENU_PANEL_CLASS}>
      {classrooms.map((classroom) => (
        <Menu.Item
          as={Link}
          className='no-underline'
          key={classroom.getId()}
          preventDefaultClickHandler={false}
          to={getSettingsPath(classroom.getId())}
        >
          {classroom.getName()}
        </Menu.Item>
      ))}
    </Menu.Content>
  </Menu>
);

interface SchoolGroup {
  schoolName: string;
  classrooms: ImportedClassroom[];
}

/**
 * Prompts a teacher who was approved at a school after importing from Clever to license the classes
 * that import created unlicensed. One banner per school, each dismissible on its own.
 */
export const UnlicensedImportsBanner = () => {
  const userId = useFluxStore(masqueradeStore, (s) => s.getUserIdByMasqueradeState() ?? '');
  const {isReady, errors} = useAreLatestQueriesReady([
    () => getUnlicensedImportsQuery() as unknown as QueryBuilder
  ]);

  if (!isReady || !userId) {
    return null;
  }
  const error = errors.find(Boolean);
  if (error) {
    logger.error('UnlicensedImportsBanner: failed to load imported classes: %o', error);
    return null;
  }

  const classrooms = getUnlicensedImportsQuery().getResource() as unknown as {
    toArray: () => ImportedClassroom[];
  } | null;
  // useAreLatestQueriesReady can flip ready with a non-list stub on a rejected fetch.
  if (typeof classrooms?.toArray !== 'function') {
    return null;
  }

  const groups = new Map<string, SchoolGroup>();
  classrooms.toArray().forEach((classroom) => {
    const school = classroom.getSchool();
    if (!school || !classroom.getMeta().isCanEnableLicense()) {
      return;
    }
    const schoolId = school.getId();
    const group = groups.get(schoolId) ?? {schoolName: school.getName(), classrooms: []};
    group.classrooms.push(classroom);
    groups.set(schoolId, group);
  });

  if (!groups.size) {
    return null;
  }

  return (
    <div className='gap-x1-half flex flex-col empty:hidden'>
      {[...groups].map(([schoolId, {schoolName, classrooms: schoolClassrooms}]) => {
        const count = schoolClassrooms.length;
        const single = count === 1 ? schoolClassrooms[0] : null;
        return (
          <Banner
            alignContent='center'
            data-testid='unlicensed-imports-banner'
            dismissable
            key={`${userId}:${schoolId}`}
            storageKey={getDismissKey(userId, schoolId)}
            type='info'
          >
            <div className='gap-x2 flex grow items-center'>
              <Icon
                className='shrink-0'
                color='inherit'
                icon='school-circle-exclamation'
                iconStyle='regular'
              />
              <div className='flex grow flex-col items-start'>
                <Text bold color='inherit'>
                  {count} {pluralize('class', count, 'classes')} imported from Clever{' '}
                  {single ? 'is' : 'are'} not using your {schoolName} license.
                </Text>
                <Text color='inherit'>
                  {single ? 'It was' : 'They were'} imported before you were approved at{' '}
                  {schoolName}. Turn on licensing in class settings so students can access licensed
                  content.
                </Text>
              </div>
              {single ? (
                <Anchor className='mr-x1 shrink-0' to={getSettingsPath(single.getId())} underlined>
                  Open class settings
                </Anchor>
              ) : (
                <ClassPicker classrooms={schoolClassrooms} />
              )}
            </div>
          </Banner>
        );
      })}
    </div>
  );
};
